import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Container,
  Card,
  CardContent
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getSubmissionTimestamp } from '../utils/submissionUtils';

interface SuccessState {
  submissionId?: string;
  uniqueId?: string;
  editCode?: string;
  editUrl?: string;
  viewUrl?: string;
  message: string;
  paymentAmount?: number;
  error?: boolean;
}

interface FormData {
  id: string;
  title: string;
  allowEditing: boolean;
  settings?: {
    redirectUrl?: string;
    requirePayment?: boolean;
    amount?: number;
    paymentAmount?: number;
    allowMultipleSubmissions?: boolean;
  };
}

const SubmissionResultPage: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as SuccessState;

  const [formData, setFormData] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch form data to check allowEditing setting
  useEffect(() => {
    const fetchFormData = async () => {
      if (!customUrl) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/forms/url/${customUrl}`);
        setFormData(response.data);
      } catch (error) {
        console.error('Error fetching form data:', error);
        // If form not found, default to allowing editing (backward compatibility)
        if (axios.isAxiosError(error) && error.response?.status === 404) {
          console.warn(`Form with URL '${customUrl}' not found, defaulting to allowEditing: true`);
          setFormData({ id: '', title: '', allowEditing: true });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFormData();
  }, [customUrl]);

  // Check for URL parameters first, then fall back to state
  const urlParams = new URLSearchParams(location.search);
  const isSuccessFromUrl = urlParams.get('success') === 'true';
  const messageFromUrl = urlParams.get('message');
  const submissionIdFromUrl = urlParams.get('submissionId');
  const uniqueIdFromUrl = urlParams.get('uniqueId');
  const editCodeFromUrl = urlParams.get('editCode');

  const isError = !isSuccessFromUrl && (state?.error || false);
  const message = messageFromUrl || state?.message || (isError ? 'Submission failed' : 'Form submitted successfully!');
  const submissionId = submissionIdFromUrl || state?.submissionId;
  const uniqueId = uniqueIdFromUrl || state?.uniqueId;
  const editCode = editCodeFromUrl || state?.editCode;
  const editUrl = state?.editUrl || (uniqueId && editCode ? `/edit-submission/${uniqueId}/${editCode}` : null);
  const viewUrl = state?.viewUrl || (uniqueId ? `/view-submission/${uniqueId}` : null);
  const paymentAmount = state?.paymentAmount;

  // Check if editing is allowed for this form
  const isEditingAllowed = formData?.allowEditing !== false;
  const shouldShowEditLink = editUrl && isEditingAllowed && !loading;

  const handleBackToForm = () => {
    navigate(`/${customUrl}`);
  };

  const handleGoHome = () => {
    // If form has a redirectUrl, redirect there instead of home
    if (formData?.settings?.redirectUrl) {
      // Check if it's a full URL or relative path
      if (formData.settings.redirectUrl.startsWith('http://') || formData.settings.redirectUrl.startsWith('https://')) {
        // External URL - use window.location
        window.location.href = formData.settings.redirectUrl;
      } else {
        // Internal path - use navigate
        navigate(formData.settings.redirectUrl);
      }
    } else {
      // Default behavior - go to home
      navigate('/');
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4, textAlign: 'center' }}>
        <Box mb={3}>
          {isError ? (
            <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          )}
        </Box>

        <Typography variant="h4" component="h1" gutterBottom>
          {isError ? 'Submission Failed' : 'Thank You!'}
        </Typography>

        <Alert 
          severity={isError ? 'error' : 'success'} 
          sx={{ mb: 3, textAlign: 'left' }}
        >
          {message}
        </Alert>

        {!isError && (
          <>
            {uniqueId && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Submission ID
                  </Typography>
                  <Typography variant="h6" component="div">
                    {getSubmissionTimestamp(uniqueId)}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Please save this ID for your records
                  </Typography>
                </CardContent>
              </Card>
            )}

            {paymentAmount && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Payment Amount
                  </Typography>
                  <Typography variant="h6" component="div" color="success.main">
                    ৳{paymentAmount}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Payment processed successfully
                  </Typography>
                </CardContent>
              </Card>
            )}

            {uniqueId && (
              <>
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="body2" color="textSecondary">
                      Unique Submission ID
                    </Typography>
                    <Typography variant="h6" component="div" fontFamily="monospace">
                      {uniqueId}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Use this ID to access your submission publicly
                    </Typography>
                  </CardContent>
                </Card>
              </>
            )}

            {editCode && isEditingAllowed && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Edit Code
                  </Typography>
                  <Typography variant="h6" component="div" fontFamily="monospace" color="primary.main">
                    {editCode}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    Use this code along with your Unique ID to edit your submission
                  </Typography>
                </CardContent>
              </Card>
            )}

            {viewUrl && (
              <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" gutterBottom>
                  <strong>View Your Submission:</strong>
                </Typography>
                <Typography variant="caption" component="div" fontFamily="monospace">
                  {window.location.origin}{viewUrl}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Bookmark this URL to view your submission status anytime
                </Typography>
              </Alert>
            )}

            {uniqueId && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body2" color="textSecondary">
                    Download Your Submission
                  </Typography>
                  <Typography variant="h6" component="div" color="primary.main" sx={{ mb: 2 }}>
                    PDF Copy Available
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(`/pdf/${uniqueId}`)}
                    sx={{ mb: 1 }}
                  >
                    View/Download PDF
                  </Button>
                  <Typography variant="caption" color="textSecondary" display="block">
                    Access your submission as a PDF document anytime using your Unique ID
                  </Typography>
                  <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                    <Typography variant="caption">
                      <strong>Note:</strong> PDF download is only available when your submission status is "completed". 
                      If payment is required, the PDF will be available after payment confirmation.
                      {paymentAmount && ' Your payment has been processed successfully.'}
                    </Typography>
                  </Alert>
                  <Typography variant="caption" component="div" fontFamily="monospace" color="primary.main" sx={{ mt: 1 }}>
                    PDF URL: {window.location.origin}/pdf/{uniqueId}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {shouldShowEditLink && (
              <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Edit Your Submission:</strong>
                </Typography>
                <Typography variant="caption" component="div" fontFamily="monospace">
                  {window.location.origin}{editUrl}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Bookmark this URL to edit your submission later
                </Typography>
              </Alert>
            )}

            {editUrl && !isEditingAllowed && (
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Note:</strong>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Editing has been disabled for this form by the administrator. 
                  Please contact the form administrator if you need to make changes to your submission.
                </Typography>
              </Alert>
            )}

            <Typography variant="body1" color="textSecondary" paragraph>
              Your submission has been received and will be processed accordingly. 
              {paymentAmount && ' A confirmation email will be sent to you shortly.'}
              {!isEditingAllowed && ' Please note that editing has been disabled for this form.'}
            </Typography>
          </>
        )}

        {isError && (
          <Typography variant="body1" color="textSecondary" paragraph>
            There was an issue processing your submission. Please try again or contact support if the problem persists.
          </Typography>
        )}

        <Box display="flex" gap={2} justifyContent="center" mt={4}>
          {isError && (
            <Button
              variant="contained"
              size="large"
              onClick={handleBackToForm}
            >
              Try Again
            </Button>
          )}
          
          <Button
            variant={isError ? "outlined" : "contained"}
            size="large"
            onClick={handleGoHome}
          >
            {isError ? 'Go Home' : 'Done'}
          </Button>
        </Box>

        <Typography variant="caption" color="textSecondary" display="block" mt={3}>
          {isError 
            ? 'If you continue to experience issues, please contact our support team.'
            : isEditingAllowed 
              ? formData?.settings?.redirectUrl 
                ? 'Thank you for your submission. Click "Done" to continue or you can close this page.'
                : 'Thank you for your submission. You can now close this page.'
              : 'Thank you for your submission. Editing is disabled for this form. You can now close this page.'
          }
        </Typography>
      </Paper>
    </Container>
  );
};

export default SubmissionResultPage;
